const BLANK_VALUES = new Set(["", "na", "n/a", "null", "undefined"]);

export const isBlankValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value !== "string") return false;
  return BLANK_VALUES.has(value.trim().toLowerCase());
};

export const toDisplayValue = (value, fallback = "-") =>
  isBlankValue(value) ? fallback : value;

export const toNumberValue = (value, fallback = 0) => {
  if (isBlankValue(value)) return fallback;
  const numberValue = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

export const formatDisplayNumber = (value) => {
  if (isBlankValue(value)) return "-";
  const numberValue = toNumberValue(value, null);
  return numberValue === null ? String(value) : numberValue.toLocaleString("en-IN");
};

const normalizeKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getLooseOwnValue = (source, key) => {
  if (!source || typeof source !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(source, key)) return source[key];

  const normalizedSearchKey = normalizeKey(key);
  const actualKey = Object.keys(source).find(
    (sourceKey) => normalizeKey(sourceKey) === normalizedSearchKey,
  );
  return actualKey ? source[actualKey] : undefined;
};

export const pickValue = (source, keys, fallback = "-") => {
  const sources = [
    source,
    source?.catalog_item,
    source?.catalogItem,
    source?.matched_item,
    source?.matchedItem,
    source?.item,
    source?.catalog,
    source?.price_catalog,
    source?.priceCatalog,
  ];

  for (const candidateSource of sources) {
    for (const key of keys) {
      const value = getLooseOwnValue(candidateSource, key);
      if (!isBlankValue(value)) return value;
    }
  }

  return fallback;
};

export const getMatchedCatalogItems = (payload) => {
  const root = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const savedRecord = root?.saved_record || payload?.saved_record || {};
  const results = root?.results || savedRecord?.results || payload?.results || {};
  const matchedResults =
    root?.matched_results ||
    savedRecord?.matched_results ||
    payload?.matched_results ||
    {};
  const filterResults = Array.isArray(matchedResults?.filter_results)
    ? matchedResults.filter_results
    : Array.isArray(results?.filter_results)
      ? results.filter_results
      : [];
  const nestedMatchedItems = filterResults.flatMap((filterResult) =>
    Array.isArray(filterResult?.matched_items) ? filterResult.matched_items : [],
  );

  if (nestedMatchedItems.length > 0) return nestedMatchedItems;
  if (Array.isArray(matchedResults?.matched_items)) return matchedResults.matched_items;
  if (Array.isArray(results?.matched_items)) return results.matched_items;
  if (Array.isArray(root?.matched_items)) return root.matched_items;
  if (Array.isArray(savedRecord?.matched_items)) return savedRecord.matched_items;
  if (Array.isArray(payload?.matched_items)) return payload.matched_items;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

export const normalizePriceCatalogItem = (item = {}, index = 0) => {
  const quantity = pickValue(item, ["quantity", "qty", "Stock Qty", "stock_qty"], "");
  const mrp = pickValue(item, ["mrp", "MRP", "list_price", "listPrice"], "");
  const discountPercent = pickValue(
    item,
    ["discount_percent", "discountPercentage", "discount_%", "Discount %", "discount"],
    "",
  );
  const unitPriceBeforeTax = pickValue(
    item,
    [
      "unit_price_before_tax",
      "unitPriceBeforeTax",
      "Unit Price Before Tax",
      "unit_price",
      "unitPrice",
      "Unit Price",
      "unit_rate",
      "rate",
    ],
    "",
  );
  const taxPercent = pickValue(
    item,
    ["tax_percent", "taxPercentage", "Tax %", "tax", "gst", "gst_percent"],
    "",
  );
  const taxAmountPerUnit = pickValue(
    item,
    [
      "tax_amount_per_unit",
      "taxAmountPerUnit",
      "tax_amount_unit",
      "Tax Amount / Unit",
      "tax_amount",
    ],
    "",
  );
  const netUnitPrice = pickValue(
    item,
    [
      "total_price",
      "net_unit_price",
      "netUnitPrice",
      "Net Unit Price",
      "net_price",
      "final_unit_price",
      "unit_price",
      "unitPrice",
      "Unit Price",
    ],
    "",
  );
  const totalLineValue = pickValue(
    item,
    [
      "total_line_value",
      "totalLineValue",
      "Total Line Value",
      "total_price",
      "totalPrice",
      "total_amount",
      "amount",
      "Amount",
    ],
    "",
  );
  const partNo = pickValue(
    item,
    ["item_code","part_no", "partNo", "part_number", "partNumber", "Part No", "sku_number", "sku"],
    "",
  );

  return {
    ...item,
    token:
      item?.token ||
      `${pickValue(item, ["filter_id", "filter_token"], "filter")}-${pickValue(
        item,
        ["row_number", "rowNo", "Line No", "line_no"],
        index + 1,
      )}-${index}`,
    row_number: pickValue(item, ["row_number", "rowNo", "Line No", "line_no"], index + 1),
    filter_name: pickValue(item, ["filter_name", "filterName", "name"], "-"),
    item_id: pickValue(
      item,
      ["item_id", "itemId", "Item ID", "part_no", "partNo", "item_code", "itemCode"],
      "-",
    ),
    part_no: toDisplayValue(partNo),
    item_code: pickValue(
      item,
      ["part_no", "partNo", "part_number", "Part No", "sku_number", "item_code", "itemCode"],
      "-",
    ),
    item_name: pickValue(item, ["item_name", "itemName", "Item Name", "name"], "-"),
    description: pickValue(
      item,
      ["description", "Description", "item_description", "Item Description"],
      "-",
    ),
    category: pickValue(item, ["category", "Category"], "-"),
    subcategory: pickValue(item, ["subcategory", "sub_category", "Subcategory"], "-"),
    brand: pickValue(item, ["brand", "Brand", "make"], "-"),
    model: pickValue(item, ["model", "Model"], "-"),
    sku_number: pickValue(
      item,
      ["sku_number", "skuNumber", "SKU Number", "sku", "SKU", "part_number"],
      "-",
    ),
    uom: pickValue(item, ["uom", "UOM", "unit", "Unit"], "-"),
    quantity: formatDisplayNumber(quantity),
    mrp: formatDisplayNumber(mrp),
    discount_percent: formatDisplayNumber(discountPercent),
    unit_price_before_tax: formatDisplayNumber(unitPriceBeforeTax),
    tax_percent: formatDisplayNumber(taxPercent),
    tax_amount_per_unit: formatDisplayNumber(taxAmountPerUnit),
    net_unit_price: formatDisplayNumber(netUnitPrice),
    total_line_value: formatDisplayNumber(totalLineValue),
    unit_price: formatDisplayNumber(netUnitPrice || unitPriceBeforeTax || mrp),
    currency: pickValue(item, ["currency", "Currency"], "-"),
    moq: formatDisplayNumber(pickValue(item, ["moq", "MOQ", "minimum_order_quantity"], "")),
    stock_qty: formatDisplayNumber(
      pickValue(item, ["stock_qty", "stockQty", "Stock Qty", "available_stock"], ""),
    ),
    lead_time: pickValue(item, ["lead_time", "leadTime", "Lead Time"], "-"),
    warranty: pickValue(item, ["warranty", "Warranty"], "-"),
    supplier: pickValue(
      item,
      ["supplier", "Supplier", "supplier_name", "vendor_name", "vendor", "company_name"],
      "-",
    ),
    effective_from: pickValue(
      item,
      ["effective_from", "effectiveFrom", "Effective From", "valid_from"],
      "-",
    ),
    valid_until: pickValue(
      item,
      ["valid_until", "validUntil", "Valid Until", "valid_to", "expiry_date"],
      "-",
    ),
    status: pickValue(item, ["status", "Status", "match_status"], "-"),
    quantity_value: toNumberValue(quantity),
    unit_price_value: toNumberValue(netUnitPrice || unitPriceBeforeTax || mrp),
    tax_percent_value: toNumberValue(taxPercent),
  };
};

export const normalizePriceCatalogItems = (items) =>
  (Array.isArray(items) ? items : []).map(normalizePriceCatalogItem);

export const normalizeFilteredCatalogResponse = (
  payload,
  priceCatalogFile,
  filterDocumentFile,
) => {
  const root = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const catalog = root?.catalog || root?.files?.price_catalog || {};
  const filterDocument =
    root?.filter_document || root?.filterDocument || root?.files?.filter_document || {};
  const companyDetails = root?.company_details || {};
  const detection = root?.detection || {};
  const extraction = root?.extraction || {};
  const results = root?.results || {};
  const matchedResults = root?.matched_results || {};
  const matchedItems = normalizePriceCatalogItems(getMatchedCatalogItems(payload));
  const filterResults = Array.isArray(matchedResults?.filter_results)
    ? matchedResults.filter_results
    : Array.isArray(results?.filter_results)
      ? results.filter_results
      : [];
  const matchedCountFromFilters = filterResults.reduce(
    (sum, filterResult) => sum + Number(filterResult?.matched_count || 0),
    0,
  );

  return {
    ...root,
    catalog,
    filter_document: filterDocument,
    company_details: companyDetails,
    detection,
    extraction,
    matched_results: {
      ...matchedResults,
      filter_results: filterResults,
      matched_items: matchedItems,
    },
    results: {
      ...results,
      matched_items: matchedItems,
    },
    document_number:
      root?.filter_file ||
      root?.catalog_file ||
      filterDocument?.file_name ||
      catalog?.file_name ||
      filterDocumentFile?.name ||
      priceCatalogFile?.name ||
      "Generated Quotation",
    buyer_name: root?.buyer_name || "-",
    vendor_name:
      root?.vendor_name || root?.supplier_name || companyDetails?.company_name || "-",
    supplier_name:
      root?.supplier_name || root?.vendor_name || companyDetails?.company_name || "-",
    supplier_address:
      root?.supplier_address ||
      root?.vendor_address ||
      companyDetails?.location ||
      "-",
    vendor_address:
      root?.vendor_address ||
      root?.supplier_address ||
      companyDetails?.location ||
      "-",
    supplier_gst: root?.supplier_gst || root?.vendor_gst || companyDetails?.gst_number || "-",
    vendor_gst: root?.vendor_gst || root?.supplier_gst || companyDetails?.gst_number || "-",
    supplier_pan: root?.supplier_pan || root?.vendor_pan || companyDetails?.pan_number || "-",
    vendor_pan: root?.vendor_pan || root?.supplier_pan || companyDetails?.pan_number || "-",
    supplier_tax: root?.supplier_tax || companyDetails?.tax || {},
    warranty: root?.warranty || companyDetails?.warranty || "-",
    created_date: new Date().toISOString().split("T")[0],
    matched_count:
      results?.total_filter_matches ??
      root?.total_filter_matches ??
      root?.matched_count ??
      (matchedCountFromFilters || undefined) ??
      matchedItems.length,
    unique_matched_count: results?.unique_matched_count ?? root?.unique_matched_count ?? "-",
    filter_set_count:
      root?.filter_set_count ?? detection?.filter_set_count ?? extraction?.filter_set_count ?? "-",
    catalog_source_type: root?.catalog_source_type || catalog?.source_type || "-",
    price_catalog_file_name:
      priceCatalogFile?.name || root?.catalog_file || catalog?.file_name || "",
    filter_document_file_name:
      filterDocumentFile?.name || root?.filter_file || filterDocument?.file_name || "",
    line_items: matchedItems,
    filter_results: filterResults,
    raw_filter_response: payload,
  };
};

export const normalizeSavedPriceCatalogRecord = (record) => {
  const savedRecord = record?.saved_record || record || {};
  const catalog =
    record?.catalog || savedRecord?.catalog || record?.files?.price_catalog || {};
  const filterDocument =
    record?.filter_document ||
    record?.filterDocument ||
    savedRecord?.filter_document ||
    record?.files?.filter_document ||
    {};
  const companyDetails = record?.company_details || savedRecord?.company_details || {};
  const detection = record?.detection || savedRecord?.detection || {};
  const extraction = record?.extraction || savedRecord?.extraction || {};
  const results = record?.results || savedRecord?.results || {};
  const matchedResults = record?.matched_results || savedRecord?.matched_results || {};
  const matchedItems = normalizePriceCatalogItems(getMatchedCatalogItems(record));
  const filterResults = Array.isArray(matchedResults?.filter_results)
    ? matchedResults.filter_results
    : Array.isArray(results?.filter_results)
      ? results.filter_results
      : Array.isArray(savedRecord?.filter_results)
        ? savedRecord.filter_results
        : [];
  const priceCatalogFileName =
    savedRecord?.price_catalog_file_name ||
    catalog?.file_name ||
    savedRecord?.catalog_file ||
    "-";
  const filterDocumentFileName =
    savedRecord?.filter_document_file_name ||
    filterDocument?.file_name ||
    savedRecord?.filter_file ||
    "-";

  return {
    ...savedRecord,
    catalog,
    filter_document: filterDocument,
    company_details: companyDetails,
    detection,
    extraction,
    matched_results: {
      ...matchedResults,
      filter_results: filterResults,
      matched_items: matchedItems,
    },
    results: {
      ...results,
      matched_items: matchedItems,
    },
    token: savedRecord?.token || savedRecord?.id || priceCatalogFileName,
    document_number: priceCatalogFileName,
    price_catalog_file_name: priceCatalogFileName,
    filter_document_file_name: filterDocumentFileName,
    catalog_source_type:
      savedRecord?.catalog_source_type || catalog?.source_type || "-",
    document_mode:
      savedRecord?.document_mode || detection?.document_mode || "-",
    filter_set_count:
      savedRecord?.filter_set_count ?? detection?.filter_set_count ?? "-",
    matched_count:
      results?.total_filter_matches ??
      savedRecord?.total_filter_matches ??
      savedRecord?.matched_count ??
      matchedItems.length,
    unique_matched_count:
      results?.unique_matched_count ?? savedRecord?.unique_matched_count ?? "-",
    created_date: savedRecord?.created_date || savedRecord?.created_at || "-",
    buyer_name: savedRecord?.buyer_name || "-",
    vendor_name:
      savedRecord?.vendor_name ||
      savedRecord?.supplier_name ||
      companyDetails?.company_name ||
      "-",
    supplier_name:
      savedRecord?.supplier_name ||
      savedRecord?.vendor_name ||
      companyDetails?.company_name ||
      "-",
    supplier_address:
      savedRecord?.supplier_address ||
      savedRecord?.vendor_address ||
      companyDetails?.location ||
      "-",
    vendor_address:
      savedRecord?.vendor_address ||
      savedRecord?.supplier_address ||
      companyDetails?.location ||
      "-",
    supplier_gst:
      savedRecord?.supplier_gst ||
      savedRecord?.vendor_gst ||
      companyDetails?.gst_number ||
      "-",
    vendor_gst:
      savedRecord?.vendor_gst ||
      savedRecord?.supplier_gst ||
      companyDetails?.gst_number ||
      "-",
    supplier_pan:
      savedRecord?.supplier_pan ||
      savedRecord?.vendor_pan ||
      companyDetails?.pan_number ||
      "-",
    vendor_pan:
      savedRecord?.vendor_pan ||
      savedRecord?.supplier_pan ||
      companyDetails?.pan_number ||
      "-",
    supplier_tax: savedRecord?.supplier_tax || companyDetails?.tax || {},
    warranty: savedRecord?.warranty || companyDetails?.warranty || "-",
    line_items: matchedItems,
    filter_results: filterResults,
    raw_filter_response: record,
  };
};
